import { markdownToPdf } from './ai-markdown-pdf.util';

/**
 * Transforma o resultado de uma execução de IA num `File`, para quem quiser gravá-lo (anexo,
 * download, upload). Sem domínio: o nome e o destino são de quem chama.
 */
export type AiResultFileFormat = 'md' | 'txt' | 'json' | 'pdf';

const MIME: Record<AiResultFileFormat, string> = {
  md: 'text/markdown',
  txt: 'text/plain',
  json: 'application/json',
  pdf: 'application/pdf',
};

/** Nome seguro para arquivo, com a extensão do formato. */
export function aiResultFileName(base: string, format: AiResultFileFormat): string {
  const safe =
    base
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w.-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80) || 'resultado-ia';
  return safe.toLowerCase().endsWith(`.${format}`) ? safe : `${safe}.${format}`;
}

/** Texto legível de markdown (para o PDF, que não renderiza markdown). */
export function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/```[\w-]*\n?/g, '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
}

/**
 * `content` é texto (md/txt) ou qualquer valor (json). O PDF é o markdown formatado em A4
 * (títulos, listas, tabelas, código — ai-markdown-pdf.util), com o `jspdf` carregado só quando pedido.
 */
export async function aiResultToFile(
  content: unknown,
  baseName: string,
  format: AiResultFileFormat,
  title?: string
): Promise<File> {
  const name = aiResultFileName(baseName, format);
  const text = typeof content === 'string' ? content : JSON.stringify(content ?? null, null, 2);

  if (format === 'json') {
    let body = text;
    try {
      body = JSON.stringify(typeof content === 'string' ? JSON.parse(content) : content, null, 2);
    } catch {
      // não é JSON válido: grava como veio
    }
    return new File([body], name, { type: MIME.json });
  }

  if (format !== 'pdf') return new File([text], name, { type: MIME[format] });

  const doc = await markdownToPdf(text, title);
  return new File([doc.output('blob')], name, { type: MIME.pdf });
}
